import React, { useState } from "react";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Landmark, Calculator, Info, Percent } from "lucide-react";

export const LiquidVsSavingsPage = () => {
  const [amount, setAmount] = useState(500000);
  const [months, setMonths] = useState(12);
  const [savingsRate, setSavingsRate] = useState(3);
  const [liquidReturn, setLiquidReturn] = useState(6.5);
  const [result, setResult] = useState(null);

  const calculate = () => {
    const P = amount;
    const n = months;
    
    // Savings Bank (simple interest, monthly compounding)
    const savingsMonthlyRate = savingsRate / 100 / 12;
    const savingsValue = P * Math.pow(1 + savingsMonthlyRate, n);
    const savingsInterest = savingsValue - P;
    
    // Liquid Fund (returns compounded)
    const liquidMonthlyRate = liquidReturn / 100 / 12;
    const liquidValue = P * Math.pow(1 + liquidMonthlyRate, n);
    const liquidGains = liquidValue - P;
    
    // Tax on liquid fund gains (20% indexation for >3Y, else slab rate)
    const taxRate = n >= 36 ? 0.20 : 0.30; // Simplified
    const liquidTax = liquidGains * taxRate;
    const liquidPostTax = liquidValue - liquidTax;
    
    // Savings interest is taxable as per slab
    const savingsTax = savingsInterest * 0.30; // Assume highest slab
    const savingsPostTax = savingsValue - savingsTax;
    
    setResult({
      principal: P,
      savingsValue: Math.round(savingsValue),
      savingsInterest: Math.round(savingsInterest),
      savingsPostTax: Math.round(savingsPostTax),
      liquidValue: Math.round(liquidValue),
      liquidGains: Math.round(liquidGains),
      liquidPostTax: Math.round(liquidPostTax),
      extraEarnings: Math.round(liquidPostTax - savingsPostTax),
      savingsEffectiveRate: ((savingsPostTax / P - 1) * 100 * 12 / n).toFixed(2),
      liquidEffectiveRate: ((liquidPostTax / P - 1) * 100 * 12 / n).toFixed(2)
    });
  };

  const formatCurrency = (num) => {
    if (num >= 10000000) return `₹${(num / 10000000).toFixed(2)} Cr`;
    if (num >= 100000) return `₹${(num / 100000).toFixed(2)} L`;
    return `₹${num.toLocaleString('en-IN')}`;
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="pt-32 pb-20">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center mb-12">
            <h1 className="font-serif text-3xl sm:text-4xl font-bold mb-4">
              Liquid Funds vs <span className="gold-text">Savings Bank</span>
            </h1>
            <p className="text-muted-foreground">
              Compare returns on your idle cash - where should you park short-term money?
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calculator className="w-5 h-5 text-primary" />
                  Parameters
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="space-y-2">
                  <Label>Amount to Park (₹)</Label>
                  <Input
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(Number(e.target.value))}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Duration (Months)</Label>
                  <Input
                    type="number"
                    value={months}
                    onChange={(e) => setMonths(Number(e.target.value))}
                    min="1"
                    max="60"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Savings Bank Rate (% p.a.)</Label>
                  <Input
                    type="number"
                    value={savingsRate}
                    onChange={(e) => setSavingsRate(Number(e.target.value))}
                    step="0.1"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Liquid Fund Return (% p.a.)</Label>
                  <Input
                    type="number"
                    value={liquidReturn}
                    onChange={(e) => setLiquidReturn(Number(e.target.value))}
                    step="0.1"
                  />
                </div>

                <Button onClick={calculate} className="w-full">
                  <Percent className="w-4 h-4 mr-2" />
                  Compare
                </Button>
              </CardContent>
            </Card>

            {result && (
              <>
                <Card className="bg-card border-border">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Landmark className="w-5 h-5 text-blue-500" />
                      Savings Bank
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="p-4 rounded-lg bg-blue-500/10">
                      <p className="text-sm text-muted-foreground">Post-Tax Value</p>
                      <p className="text-2xl font-bold text-blue-500">{formatCurrency(result.savingsPostTax)}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <p className="text-muted-foreground">Pre-Tax Value</p>
                        <p className="font-medium">{formatCurrency(result.savingsValue)}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Interest</p>
                        <p className="font-medium text-green-500">{formatCurrency(result.savingsInterest)}</p>
                      </div>
                    </div>
                    <div className="pt-3 border-t border-border text-sm space-y-1">
                      <p className="flex justify-between"><span>Effective Rate:</span> <span>{result.savingsEffectiveRate}% p.a.</span></p>
                      <p className="flex justify-between"><span>Liquidity:</span> <span className="text-green-500">Instant</span></p>
                      <p className="flex justify-between"><span>Risk:</span> <span className="text-green-500">Zero</span></p>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-card border-border">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Percent className="w-5 h-5 text-green-500" />
                      Liquid Fund
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="p-4 rounded-lg bg-green-500/10">
                      <p className="text-sm text-muted-foreground">Post-Tax Value</p>
                      <p className="text-2xl font-bold text-green-500">{formatCurrency(result.liquidPostTax)}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <p className="text-muted-foreground">Pre-Tax Value</p>
                        <p className="font-medium">{formatCurrency(result.liquidValue)}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Gains</p>
                        <p className="font-medium text-green-500">{formatCurrency(result.liquidGains)}</p>
                      </div>
                    </div>
                    <div className="pt-3 border-t border-border text-sm space-y-1">
                      <p className="flex justify-between"><span>Effective Rate:</span> <span>{result.liquidEffectiveRate}% p.a.</span></p>
                      <p className="flex justify-between"><span>Liquidity:</span> <span className="text-amber-500">T+1 day</span></p>
                      <p className="flex justify-between"><span>Risk:</span> <span className="text-green-500">Very Low</span></p>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </div>

          {result && result.extraEarnings > 0 && (
            <Card className="mt-8 bg-gradient-to-r from-green-500/10 to-green-500/5 border-green-500/20">
              <CardContent className="pt-6">
                <div className="text-center">
                  <p className="text-lg">By choosing Liquid Funds, you earn</p>
                  <p className="text-3xl font-bold text-green-500 my-2">{formatCurrency(result.extraEarnings)} more</p>
                  <p className="text-sm text-muted-foreground">over {months} months compared to Savings Bank</p>
                </div>
              </CardContent>
            </Card>
          )}

          <Card className="mt-8 bg-card border-border">
            <CardContent className="pt-6">
              <div className="flex gap-3">
                <Info className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                <div className="text-sm text-muted-foreground space-y-2">
                  <p><strong>Liquid Funds:</strong> Invest in very short-term debt securities. Highly safe with better returns than savings accounts.</p>
                  <p><strong>Best for:</strong> Emergency funds, short-term goals, parking money between investments.</p>
                  <p><strong>Note:</strong> Returns shown are indicative. Actual liquid fund returns may vary.</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>

      <Footer />
    </div>
  );
};
